import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { type CarouselApi } from "@/components/ui/carousel";
import { useEffect, useState } from "react";
import FeatureCard, { type FeatureSlide } from "./FeatureCard";

interface FeatureCarouselProps {
  slides: FeatureSlide[];
  className?: string;
  autoplay?: boolean;
  autoplayDelay?: number;
}

export default function FeatureCarousel({ 
  slides, 
  className = "",
  autoplay = false,
  autoplayDelay = 5000 
}: FeatureCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  // Autoplay functionality
  useEffect(() => {
    if (!api || !autoplay) {
      return;
    }

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, autoplayDelay);

    return () => clearInterval(interval);
  }, [api, autoplay, autoplayDelay]);

  return (
    <div className={`w-full ${className}`}>
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
          skipSnaps: false,
          dragFree: false,
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {slides.map((slide) => (
            <CarouselItem 
              key={slide.id} 
              className="pl-2 md:pl-4 basis-full md:basis-[85%]"
            >
              <div className="h-full">
                <FeatureCard slide={slide} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        {/* Navigation Arrows - Hidden */}
        <CarouselPrevious className="hidden" />
        <CarouselNext className="hidden" />
      </Carousel>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 md:mt-8 gap-2">
        {Array.from({ length: count }, (_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index + 1 === current 
                ? 'bg-[#FF7A00] w-6' 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            onClick={() => api?.scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Mobile slide counter */}
      <div className="md:hidden text-center mt-4 text-sm text-gray-500 font-manrope">
        {current} of {count}
      </div>
    </div>
  );
}

